/**
 * masterのStateを扱うモジュール
 */
import { ThunkDispatch } from 'redux-thunk';

import { ActionsUnion, createAction } from 'common/utils/action-utils';
import { beginFetch, endFetch, failedFetch } from 'common/modules/app/fetch';
import { fetchFailure, ErrorResponse } from 'common/modules/app/errors';
import { request } from 'common/utils/request/http-request';
import {
  getLanguageRequest,
  getCountryMasterRequest
} from 'common/utils/request/static-request';
import {
  GetCountryMasterResponse,
  GetLanguageMasterResponse
} from 'common/utils/request/static-response';

import { AppState } from 'pages';

enum LanguageActionTypes {
  FETCH_SUCCESS = 'app/master/language/FETCH_SUCCESS'
}

enum CountryActionTypes {
  FETCH_SUCCESS = 'app/master/country/FETCH_SUCCESS'
}

// マスター取得のリクエストID
const GET_LANGUAGE_ID = 'get_language_master';
const GET_COUNTRY_ID = 'get_country_master';

/**
 * Languageの取得成功のActionを作成する
 * @param {Language} payload 取得したLanguage
 */
export const fetchSuccessLanguage = (
  payload: Language
): { type: LanguageActionTypes; payload: Language } =>
  createAction(LanguageActionTypes.FETCH_SUCCESS, payload);

/**
 * Countryの取得成功のActionを作成する
 * @param {Country} payload 取得したCountry
 */
export const fetchSuccessCountry = (
  payload: Country
): { type: CountryActionTypes; payload: Country } =>
  createAction(CountryActionTypes.FETCH_SUCCESS, payload);

type DispatchType = ThunkDispatch<Record<string, unknown>, void, any>;

/**
 * 言語マスターを取得する
 */
export const getLanguageMaster = (): ((
  dispatch: DispatchType,
  getState: () => AppState
) => Promise<void>) => {
  return async (dispatch: DispatchType) => {
    // 該当リクエストIDの状態を処理中に変更
    dispatch(beginFetch(GET_LANGUAGE_ID));
    const requestData = getLanguageRequest();
    const response = await request(requestData.url, requestData.config);
    // API実行結果がエラーかどうか判断
    if (response.error) {
      // エラー処理
      const error = response.error as ErrorResponse;
      error.requestId = GET_LANGUAGE_ID;
      dispatch(fetchFailure(error));
      dispatch(failedFetch(GET_LANGUAGE_ID));
      return;
    } else {
      // APIが成功した場合は取得した言語マスターででstateを更新
      const body = (response.body as unknown) as GetLanguageMasterResponse;
      dispatch(fetchSuccessLanguage(body.languages));
    }
    // 該当リクエストIDの状態を未処理に変更
    dispatch(endFetch(GET_LANGUAGE_ID));
  };
};

/**
 * 国マスターを取得する
 */
export const getCountryMaster = (): ((
  dispatch: DispatchType,
  getState: () => AppState
) => Promise<void>) => {
  return async (dispatch: DispatchType) => {
    // 該当リクエストIDの状態を処理中に変更
    dispatch(beginFetch(GET_COUNTRY_ID));
    const requestData = getCountryMasterRequest();
    const response = await request(requestData.url, requestData.config);
    // API実行結果がエラーかどうか判断
    if (response.error) {
      // エラー処理
      const error = response.error as ErrorResponse;
      error.requestId = GET_COUNTRY_ID;
      dispatch(fetchFailure(error));
      dispatch(failedFetch(GET_COUNTRY_ID));
      return;
    } else {
      // APIが成功した場合は取得したで国マスターでstateを更新
      const body = (response.body as unknown) as GetCountryMasterResponse;
      dispatch(fetchSuccessCountry(body.region));
    }
    // 該当リクエストIDの状態を未処理に変更
    dispatch(endFetch(GET_COUNTRY_ID));
  };
};

const actions = {
  fetchSuccessLanguage,
  fetchSuccessCountry
};
type MasterAction = ActionsUnion<typeof actions>;

/**
 * MasterのState
 */
export interface Master {
  /** Masterの情報 */
  language: Language;
  country: Country;
}

const initialState: Master = {
  language: {},
  country: {}
};

/**
 * LanguageマスターのState
 */
interface Language {
  [languageCode: string]: { code: string; glossary: string };
}

/**
 * CountryマスターのState
 */
interface Country {
  [regionCode: string]: {
    glossary: string;
    countries: { code: string; glossary: string }[];
  };
}

export default (
  state: Master = initialState,
  actions: MasterAction
): Master => {
  switch (actions.type) {
    case LanguageActionTypes.FETCH_SUCCESS:
      return {
        ...state,
        language: actions.payload
      };
    case CountryActionTypes.FETCH_SUCCESS:
      return {
        ...state,
        country: actions.payload
      };
    default:
      return state;
  }
};
